import { ComponentType, useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { cn, scrollBarClasses } from 'shared/lib';
import { FaRegFaceSmile } from 'react-icons/fa6';
import {
  EPersonaIntegrationMode,
  ITavusPersona,
  ITavusPersonaFormik,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { AvatarEditPersonaForm } from 'features/avatar-edit-persona-form';
import StockImagesProvider from 'features/list-stock-images/model/useStockImages';
import { AVATARS_CREATE_PERSONA_ROUTE } from 'shared/model';
import { PreconversationEditForm } from 'features/preconversation-edit-form';
import { AvatarChoose } from 'features/avatar-choose';
import {
  Code,
  Code1,
  CpuSetting,
  Icon,
  Personalcard,
  User,
  UserTag
} from 'iconsax-reactjs';
import { ChooseBackground } from 'widgets/choose-background';
import { Background } from 'shared/icons';
import { useLocation } from 'react-router';
import { EmbedForm } from 'features/tavus-embed-persona';
import { WidgetForm } from 'features/tavus-widget-persona';
import { MdOutlineFiberSmartRecord } from 'react-icons/md';
import TavusRecord from 'features/tavus-record/TavusRecord';

type Tool = {
  name: string;
  icon: Icon;
  disabled: boolean;
};

type Props = {
  formik: FormikProps<ITavusPersonaFormik>;
  persona: ITavusPersona;
  IdentityFormComponent: ComponentType<{
    formik: FormikProps<ITavusPersonaFormik>;
  }>;
};

export const AvatarEditPersonaSidebar = ({
  formik,
  persona,
  IdentityFormComponent
}: Props) => {
  const { pathname } = useLocation();
  const { setSideBarOpened, sideBarOpened } = tavusPersonaStore((state) => ({
    setSideBarOpened: state.setSideBarOpened,
    sideBarOpened: state.sideBarOpened
  }));

  const tools: Tool[] = [
    {
      name: 'Avatar',
      icon: User,
      disabled: false
    },
    {
      name: 'Identity',
      icon: UserTag,
      disabled: false
    },
    {
      name: 'Persona',
      icon: Personalcard,
      disabled: false
    },
    {
      name: 'Background',
      icon: Background,
      disabled: false
    },
    {
      name: 'Preconversation',
      icon: CpuSetting,
      disabled: false
    },
    {
      name: 'Widget',
      icon: Code,
      disabled: !!pathname.includes(AVATARS_CREATE_PERSONA_ROUTE)
    },
    {
      name: 'Embed',
      icon: Code1,
      disabled: !!pathname.includes(AVATARS_CREATE_PERSONA_ROUTE)
    },
    {
      name: 'Recording',
      icon: MdOutlineFiberSmartRecord,
      disabled: !!pathname.includes(AVATARS_CREATE_PERSONA_ROUTE)
    }
  ];

  const [tool, setTool] = useState(tools[0].name);
  const { setPersonaIntegrationViewMode } = tavusPersonaStore();

  const [customImageBackgrounds, setCustomImageBackgrounds] = useState<
    string[]
  >([]);

  const [customVideoBackgrounds, setCustomVideoBackgrounds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (tool === tools[5].name) {
      setPersonaIntegrationViewMode(EPersonaIntegrationMode.WIDGET);
    } else if (tool === tools[6].name) {
      setPersonaIntegrationViewMode(EPersonaIntegrationMode.EMBED);
    }
  }, [tool]);

  return (
    <div
      className={`${
        sideBarOpened
          ? 'ag-w-full lg:ag-w-[644px] ag-opacity-100'
          : 'ag-w-0 ag-overflow-hidden ag-opacity-0 lg:ag-opacity-100 -ag-left-[999px] lg:ag-left-0 lg:ag-w-[644px]'
      } max-lg:ag-overflow-auto ag-absolute ag-z-[60] lg:ag-relative ag-left-0 ag-bottom-0 ag-top-14 ag-duration-200 lg:ag-w-[644px] lg:ag-gap-2 ag-flex  ag-flex-col lg:ag-flex-row`}
    >
      <div className="lg:ag-h-full ag-flex ag-flex-row ag-flex-wrap ag-justify-between lg:ag-justify-normal ag-gap-1 lg:ag-flex-col ag-px-4 ag-py-6 ag-bg-white">
        {tools.map(
          (t) =>
            !t.disabled && (
              <button
                type="button"
                onClick={() => {
                  setTool(t.name);
                  setSideBarOpened(true);
                }}
                key={t.name}
                className={`ag-rounded-[5.63px] ag-duration-200 hover:ag-bg-light-purple ${cn(
                  'ag-size-[52px] ag-relative ag-p-2 lg:ag-mb-4 ag-cursor-pointer ag-flex ag-items-center ag-justify-center ag-group',
                  t.name === tool && 'ag-bg-light-purple ag-duration-200'
                )}`}
              >
                <t.icon
                  className="ag-text-slate-600 ag-transition-colors ag-group-hover:ag-text-dark-purple"
                  size={22.5}
                />
                <div className="ag-hidden lg:ag-flex lg:ag-absolute ag-pointer-events-none ag-text-vivid-purple ag-top-14 ag-right-0 lg:ag-top-[14px] lg:ag-left-full ag-ml-2 ag-opacity-0 group-hover:ag-opacity-100 ag-transition-opacity ag-text-xs ag-z-10">
                  <span className="ag-bg-white ag-px-3 ag-py-1.5 ag-shadow ag-whitespace-nowrap ag-rounded">
                    {t.name}
                  </span>
                </div>
              </button>
            )
        )}
      </div>
      <div
        className={cn(
          `${
            sideBarOpened ? 'ag-px-4' : 'ag-px-0'
          } ag-py-3 lg:ag-my-2 ag-overflow-y-hidden lg:ag-p-6 ag-bg-white lg:ag-rounded-[10px] ag-flex-1`,
          scrollBarClasses
        )}
      >
        {tool === tools[0].name && <AvatarChoose />}
        {tool === tools[1].name && <IdentityFormComponent formik={formik} />}
        {tool === tools[2].name && <AvatarEditPersonaForm formik={formik} />}
        {tool === tools[3].name && (
          <StockImagesProvider>
            <ChooseBackground
              selectedBackground={formik.values.custom_background as string}
              onChange={(color: string | null) => {
                formik.setFieldValue('custom_background', color);
              }}
              theme={'light'}
              customImageBackgrounds={customImageBackgrounds}
              customVideoBackgrounds={customVideoBackgrounds}
              setCustomImageBackgrounds={setCustomImageBackgrounds}
              setCustomVideoBackgrounds={setCustomVideoBackgrounds}
            />
          </StockImagesProvider>
        )}
        {tools[4] && tool === tools[4].name && (
          <PreconversationEditForm formik={formik} />
        )}
        {tools[5] && tool === tools[5].name && (
          <WidgetForm formik={formik} persona={persona} />
        )}
        {tools[6] && tool === tools[6].name && (
          <EmbedForm formik={formik} persona={persona} />
        )}
        {tools[7] && tool === tools[7].name && <TavusRecord formik={formik} />}
      </div>
    </div>
  );
};
